import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import "./projects.css";
import "./shared.css";
import ConfirmBox from "./confirmBox";
import { getTranslations } from "../Shared/translations";

interface Project {
  id: number;
  userid: string;
  name: string;
  customerId: number;
  createdAt: any;
  updatedAt: any;
}

interface Customer {
  id: number;
  user_id: number;
  naam: string;
  voornaam: string;
}

interface ProjectFromDatabase {
  success: Project[];
}

interface Props {
  visible: string;
  projects: ProjectFromDatabase;
  onDataChange: (t: any) => any;
  onOpenProject: (id: number) => any;
  onDeleteProject: (id: number) => any;
}

type SortDirection = "asc" | "desc" | "none";

const Projects: React.FC<Props> = ({
  visible,
  projects,
  onDataChange,
  onOpenProject,
  onDeleteProject,
}) => {
  const t = getTranslations();
  const [selectedRow, setSelectedRow] = useState<number>(0);
  const [projToOpen, setProjToOpen] = useState<string>("0");
  const [projlist, setProjlist] = useState<ProjectFromDatabase>(projects);
  const [projToFind, setProjToFind] = useState<string>("");
  const [klantToFind, setKlantToFind] = useState<string>("");
  const [confirmBoxVisible, setConfirmBoxVisible] = useState<boolean>(false);
  const [idToDelete, setIdToDelete] = useState<number>(0);
  const [sortDirection, setSortDirection] = useState<SortDirection>("none");

  const vstate = { settings: "hidden" };

  useEffect(() => {
    setProjlist(projects);
  }, [projects]);

  const handleClose = () => {
    onDataChange(vstate);
  };

  const handleOpen = () => {
    onDataChange(vstate);
    onOpenProject(parseInt(projToOpen));
    setName(parseInt(projToOpen));
  };

  const setName = (id: number) => {
    const p = projects.success;
    const project = p.find((item) => item.id === id);
    if (project) {
      sessionStorage.setItem("bestandsnaam", project.name);
    }
  };

  const handleSetProject = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const id = e.currentTarget.id;
    setProjToOpen(id);
    setSelectedRow(Number(id));
  };

  const deleteProject = (id: number) => {
    setIdToDelete(id);
    setConfirmBoxVisible(true);
  };

  const handleDeleteConfirm = async (projectid: number) => {
    setConfirmBoxVisible(false);
    const deleted = await onDeleteProject(projectid);
    setProjlist(deleted);
    renderProjectsList();
  };

  const handleDeleteClose = () => {
    setConfirmBoxVisible(false);
  };

  const handleSortByDate = () => {
    setSortDirection((prev) => {
      if (prev === "none" || prev === "desc") return "asc";
      return "desc";
    });
  };

  const findCustomer = (id: number): Customer[] => {
    const raw = sessionStorage.getItem("DbCustommers");
    if (!raw) return [];
    try {
      const customers: Customer[] = JSON.parse(raw);
      if (!Array.isArray(customers)) return [];
      return customers.filter((c) => c.id === id);
    } catch (e) {
      console.error("Failed to parse DbCustommers from sessionStorage:", e);
      return [];
    }
  };

  const SortIcon = () => {
    if (sortDirection === "asc")
      return <ArrowUpwardIcon className="sort-icon-active" />;
    if (sortDirection === "desc")
      return <ArrowDownwardIcon className="sort-icon-active" />;
    return <UnfoldMoreIcon className="sort-icon-inactive" />;
  };

  const renderProjectsList = () => {
    if (Array.isArray(projlist.success)) {
      const filteredProjects = projlist.success.filter((offerteItem) => {
        const customer = findCustomer(offerteItem.customerId);
        const customerName = customer.length > 0 ? customer[0].naam : "";
        const matchesProject =
          projToFind === "" ||
          offerteItem.name.toLowerCase().includes(projToFind.toLowerCase());
        const matchesCustomer =
          klantToFind === "" ||
          customerName.toLowerCase().includes(klantToFind.toLowerCase());
        return matchesProject && matchesCustomer;
      });

      const tableData = filteredProjects.map((offerteItem) => {
        const customer = findCustomer(offerteItem.customerId);
        const customerName = customer.length > 0 ? customer[0].naam : "";
        const creation = offerteItem.createdAt.replace("T", " ").slice(0, -5);
        const updated = offerteItem.updatedAt.replace("T", " ").slice(0, -5);
        return {
          id: offerteItem.id,
          name: offerteItem.name,
          customer: customerName,
          creation,
          updated,
          rawCreatedAt: offerteItem.createdAt,
        };
      });

      if (sortDirection !== "none") {
        tableData.sort((a, b) => {
          const dateA = new Date(a.rawCreatedAt).getTime();
          const dateB = new Date(b.rawCreatedAt).getTime();
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        });
      }

      return (
        <div className="projects-table-wrapper">
          <table className="projects-table">
            <thead>
              <tr>
                <th
                  className="projects-th projects-th-sortable"
                  onClick={handleSortByDate}
                  title={t.sortByDate}
                >
                  {t.datum} <SortIcon />
                </th>
                <th className="projects-th" style={{ width: "40%" }}>
                  <div>{t.project}</div>
                  <TextField
                    size="small"
                    placeholder={t.filter}
                    value={projToFind}
                    onChange={(e) => setProjToFind(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="projects-filter-field"
                  />
                </th>
                <th className="projects-th">
                  <div>{t.klant}</div>
                  <TextField
                    size="small"
                    placeholder={t.filter}
                    value={klantToFind}
                    onChange={(e) => setKlantToFind(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="projects-filter-field"
                  />
                </th>
                <th className="projects-th" style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="projects-empty">
                    {t.noProjectsFound}
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => (
                  <tr
                    key={row.id}
                    id={String(row.id)}
                    onClick={handleSetProject}
                    className={[
                      "projects-tr",
                      index % 2 === 0 ? "projects-tr-even" : "projects-tr-odd",
                      selectedRow === row.id ? "projects-tr-selected" : "",
                    ]
                      .join(" ")
                      .trim()}
                  >
                    <td className="projects-td">
                      <span className="projects-date">{row.creation}</span>
                    </td>
                    <td className="projects-td projects-td-name">{row.name}</td>
                    <td className="projects-td projects-td-customer">
                      {row.customer}
                    </td>
                    <td className="projects-td projects-td-delete">
                      <span
                        className="projects-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteProject(row.id);
                        }}
                        title={t.deleteProject}
                      >
                        <DeleteOutlineIcon className="projects-delete-icon" />
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }
  };

  return (
    <>
      <Modal show={visible === "open"} dialogClassName="modal_projects">
        <Modal.Header closeButton onClick={handleClose} className="modal-header-app">
          <Modal.Title>
            <span className="modal-title-app">{t.projects}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="projects-modal-body">
          {renderProjectsList()}
        </Modal.Body>
        <Modal.Footer className="modal-footer-app">
          <Button variant="secondary" onClick={handleClose} className="btn-secondary-app">
            {t.cancel}
          </Button>
          <Button variant="primary" onClick={handleOpen} className="btn-primary-app">
            {t.openProject}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmBox
        visible={confirmBoxVisible}
        message={t.projectDeleteConfirm}
        closeBox={handleDeleteClose}
        confirmDelete={handleDeleteConfirm}
        idToDelete={idToDelete}
      />
    </>
  );
};

export default Projects;
